// src/modules/config/components/tables/CategoriesTable.tsx
import React from 'react';
import {
  Table,
  Space,
  Popconfirm,
  Tag,
  Tooltip,
  Button,
  Badge
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { MenuCategory } from '@/types';

interface CategoriesTableProps {
  categories: MenuCategory[];
  loading: boolean;
  onEdit: (category: MenuCategory) => void;
  onDelete: (categoryId: number) => void;
}

export const CategoriesTable: React.FC<CategoriesTableProps> = ({
  categories,
  loading,
  onEdit,
  onDelete
}) => {

  const columns = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      width: 120,
      resizable: true,
      fixed: 'left' as const,
      render: (text: string, record: MenuCategory) => (
        <div>
          <div style={{ fontWeight: record.parent ? 'normal' : 'bold' }}>
            {record.parent ? `— ${text}` : text}
          </div>
          {record.name_kz && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.parent ? `— ${record.name_kz}` : record.name_kz}
            </div>
          )}
          {record.name_en && (
            <div style={{ fontSize: '12px', color: '#999' }}>
              {record.parent ? `— ${record.name_en}` : record.name_en}
            </div>
          )}
        </div>
      ),
      ellipsis: true
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      width: 120,
      resizable: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <code style={{ 
            fontSize: '12px',
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {text}
          </code>
        </Tooltip>
      )
    },
    {
      title: 'Иерархия',
      key: 'hierarchy',
      width: 60,
      resizable: true,
      render: (_: any, record: MenuCategory) => {
        const parentCategory = categories.find(c => c.id === record.parent);
        return record.parent ? (
          <Tag color="blue" style={{ fontSize: '11px' }}>
            {parentCategory?.name || 'Неизвестно'}
          </Tag>
        ) : (
          <Tag color="green" style={{ fontSize: '11px' }}>
            Корневая
          </Tag>
        );
      }
    },
    {
      title: 'Порядок',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 20,
      resizable: true,
      align: 'center' as const,
      sorter: (a: MenuCategory, b: MenuCategory) => a.sort_order - b.sort_order
    },
    {
      title: 'Блюд',
      dataIndex: 'items_count',
      key: 'items_count',
      width: 20,
      resizable: true,
      align: 'center' as const,
      render: (count: number) => <Badge count={count} color="blue" />
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 20,
      resizable: true,
      align: 'center' as const,
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'} style={{ fontSize: '11px' }}>
          {active ? 'Активна' : 'Неактивна'}
        </Tag>
      )
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      resizable: true,
      fixed: 'right' as const,
      render: (_: any, record: MenuCategory) => (
        <Space>
          <Tooltip title="Редактировать">
            <Button
              type="link"
              icon={<EditOutlined />}
              size="small"
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Удалить категорию?"
            description="Это действие нельзя отменить"
            onConfirm={() => onDelete(record.id)}
            okText="Удалить"
            cancelText="Отмена"
          >
            <Tooltip title="Удалить">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={categories}
      rowKey="id"
      loading={loading}
      scroll={{ 
        x: 460 // Сумма всех колонок: 120+120+60+20+20+20+100 = 460px
      }}
      pagination={{
        pageSize: 100, // Увеличили чтобы показать больше строк
        showSizeChanger: true,
        showTotal: (total, range) => 
          `${range[0]}-${range[1]} из ${total} категорий`,
        responsive: true
      }}
      size="small"
    />
  );
};